// @ts-check
// Revisão de ativação do bot (contrato de bots, seções 3.2, 17 e 22.2): os gatilhos e os números, os conflitos com
// outros bots ativos (com o link para o outro bot), os avisos (automação de boas-vindas ligada, palavra-chave repetida,
// número marcado como captação para um bot de atendimento, departamento sem membros) e as pendências. Conflito ou
// pendência de erro desabilitam "Ativar bot".
import { html } from 'htm/preact';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { blockingBotProblems } from '../../lib/bot-validation.js';
import { botEditorHref } from '../../lib/bot-list.js';
import { FlowDialog } from '../flows/flow-dialog.js';
import { FlowNotice } from '../flows/flow-ui.js';
import { botProblemText, describeBotTrigger } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/bots.js').BotActivationPreview} BotActivationPreview
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('./bot-lookups.js').BotLookupData} BotLookupData
 */

/**
 * Texto de um aviso da ativação.
 * @param {{ code: string, details?: Record<string, any> }} warning
 */
export function botActivationWarningText(warning) {
  const details = warning.details ?? {};
  switch (warning.code) {
    case 'WELCOME_AUTOMATION_ACTIVE': return t('bots.activation.warnings.WELCOME_AUTOMATION_ACTIVE', { name: details.automationName ?? t('bots.activation.welcomeAutomation') });
    case 'KEYWORD_OVERLAP': return t('bots.activation.warnings.KEYWORD_OVERLAP', { name: details.botName ?? '', keywords: (details.keywords ?? []).map((/** @type {string} */ word) => word.toLowerCase()).join(', ') });
    case 'NO_TEAM_MEMBERS': return t('bots.activation.warnings.NO_TEAM_MEMBERS', { team: details.teamName ?? '' });
    case 'CHANNEL_PURPOSE_CAPTURE': return t('bots.activation.warnings.CHANNEL_PURPOSE_CAPTURE', { channel: details.channelLabel ?? '' });
    default: return t('bots.activation.warnings.UNKNOWN');
  }
}

/**
 * A revisão permite ativar? Sem conflito e sem pendência de erro.
 * @param {BotActivationPreview} preview
 */
export const canActivateFromPreview = (preview) => preview.conflicts.length === 0 && blockingBotProblems(preview.problems).length === 0;

/**
 * @param {{ preview: BotActivationPreview, definition: BotDefinition, lookupData: BotLookupData, isBusy: boolean, onActivate: () => void, onClose: () => void,
 *   onEditProblems: () => void }} props
 */
export function BotActivationModal({ preview, definition, lookupData, isBusy, onActivate, onClose, onEditProblems }) {
  const blocking = blockingBotProblems(preview.problems);
  const canActivate = canActivateFromPreview(preview);
  const channelLabel = (/** @type {string} */ id) => preview.channels.find((channel) => channel.id === id)?.label ?? lookupData.channels?.find((channel) => channel.id === id)?.label ?? id;
  const teamName = (/** @type {string} */ id) => lookupData.teams?.find((team) => team.id === id)?.name ?? null;
  return html`<${FlowDialog} title=${t('bots.activation.title')} size="medium" onClose=${onClose} testId="bot-activation-modal"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('bots.activation.activate')} accent="blue" size="small" disabled=${!canActivate} isLoading=${isBusy} onClick=${onActivate}
        data-testid="bot-activation-confirm" />`}>
    <section class="bot-activation-section">
      <h3 class="bot-activation-title"><${Icon} name="IconPlayerPlay" size=${16} />${t('bots.activation.triggers')}</h3>
      ${definition.triggers.length === 0
        ? html`<p class="flow-dialog-text">${t('bots.activation.noTriggers')}</p>`
        : html`<ul class="bot-dialog-list">${definition.triggers.map((trigger) => {
            const described = describeBotTrigger(trigger, { channelLabel, teamName });
            return html`<li key=${trigger.id}><strong>${described.title}</strong>${described.channels ? ` ${t('bots.activation.onChannels', { channels: described.channels })}` : ''}
              ${described.details.length > 0 ? html`<span class="bot-dialog-detail">${described.details.join(' · ')}</span>` : null}</li>`;
          })}</ul>`}
    </section>
    ${preview.conflicts.length > 0
      ? html`<section class="bot-activation-section" data-tone="error" data-testid="bot-activation-conflicts">
          <h3 class="bot-activation-title"><${Icon} name="IconAlertCircle" size=${16} />${t('bots.activation.conflicts')}</h3>
          <ul class="bot-dialog-list">${preview.conflicts.map((conflict) => html`<li key=${`${conflict.botId}-${conflict.triggerKind}`}>
            ${t('bots.activation.conflict', { trigger: t(`bots.triggers.kinds.${conflict.triggerKind}`), channel: channelLabel(conflict.channelId) })}${' '}<a class="flow-summary-link" href=${botEditorHref(conflict.botId)} target="_blank" rel="noopener">${conflict.botName}</a>
          </li>`)}</ul>
          <p class="flow-field-hint">${t('bots.activation.conflictHint')}</p>
        </section>`
      : null}
    ${preview.warnings.length > 0
      ? html`<section class="bot-activation-section" data-testid="bot-activation-warnings">
          <h3 class="bot-activation-title"><${Icon} name="IconAlertTriangle" size=${16} />${t('bots.activation.warningsTitle')}</h3>
          ${preview.warnings.map((warning) => html`<${FlowNotice} key=${warning.code} tone="warning" icon="IconAlertTriangle">${botActivationWarningText(warning)}<//>`)}
        </section>`
      : null}
    <section class="bot-activation-section" data-tone=${blocking.length > 0 ? 'error' : undefined}>
      <h3 class="bot-activation-title">
        <${Icon} name=${blocking.length > 0 ? 'IconAlertCircle' : 'IconCircleCheck'} size=${16} />
        ${blocking.length > 0 ? t('bots.activation.problems', { count: blocking.length }) : t('bots.activation.ready')}
      </h3>
      ${blocking.length > 0
        ? html`<ul class="bot-dialog-list">${blocking.slice(0, 6).map((problem) => html`<li key=${`${problem.code}-${problem.nodeId ?? ''}-${problem.triggerId ?? ''}`}>${botProblemText(problem)}</li>`)}</ul>
          <${Button} title=${t('bots.activation.fix')} variant="secondary" size="small" onClick=${onEditProblems} />`
        : html`<p class="flow-dialog-text">${t('bots.activation.readyText')}</p>`}
    </section>
  <//>`;
}
